import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import * as Styled from "./Pagination.styled";
import { PaginationPage } from "./PaginationPage";
import { PaginationArrow } from "./PaginationArrow";
const MIN_LIMIT = 4;
const DEFAULT_LIMIT = 6;
const PaginationComponent = forwardRef((props, ref) => {
    const { activeIndex = 0, alwaysShowArrows, ariaLabelArrowNext = "Next", ariaLabelArrowPrev = "Previous", ariaLabelDisabled = "Disabled", ariaLabelPagePrefix = "Go to page ", ariaLabelWrapper = "Pagination", arrowLinkNext, arrowLinkPrev, children, getPageProps = (index) => ({ id: String(index) }), limit, linkComponent, locale, numPages, onPageClick, pages = [], renderCaption, ...otherProps } = props;
    if (typeof limit !== "number" || limit < MIN_LIMIT) {
        throw new Error(`Limit must be a number equal to or greater than ${MIN_LIMIT}`);
    }
    const getVisiblePages = () => {
        const visiblePages = numPages
            ? Array.from({ length: numPages }, (_, pageIndex) => ({ pageIndex }))
            : pages.map((page, pageIndex) => ({
                page,
                pageIndex
            }));
        if (limit && visiblePages.length > limit) {
            const overFlowIndicator = {
                isDisabled: true,
                label: "…",
                value: "…"
            };
            if (activeIndex < visiblePages.length - limit + 2) {
                // Truncate the pagination on the right
                const rightOverflow = activeIndex < limit - 3 ? visiblePages.length - limit + 1 : visiblePages.length - (activeIndex + 2);
                visiblePages.splice(-1 - rightOverflow, rightOverflow);
                // Add the overflow indicator
                visiblePages.splice(-1, 0, {
                    isOverflow: true,
                    page: { ...overFlowIndicator, key: "overflowRight" }
                });
            }
            if (visiblePages.length > limit) {
                // Truncate the pagination on the left
                const leftOverflow = visiblePages.length - limit;
                if (limit > 4) {
                    visiblePages.splice(1, leftOverflow + 1);
                    // Add the overflow indicator
                    visiblePages.splice(1, 0, {
                        isOverflow: true,
                        page: { ...overFlowIndicator, key: "overflowLeft" }
                    });
                }
                if (limit === 4) {
                    visiblePages.splice(1, leftOverflow);
                    visiblePages.splice(0, 0, {
                        isOverflow: true,
                        page: { ...overFlowIndicator, key: "overflowLeft" }
                    });
                    // remove the first item
                    visiblePages.splice(1, 1);
                }
            }
        }
        return visiblePages;
    };
    const totalPages = numPages || pages.length;
    const hasBorders = alwaysShowArrows || totalPages > MIN_LIMIT;
    const visiblePages = getVisiblePages();
    return (_jsxs(Styled.PaginationWrapper, { ref: ref, "aria-label": ariaLabelWrapper, "data-testid": "Pagination", ...otherProps, children: [_jsxs(Styled.Pagination, { children: [hasBorders && (_jsx(PaginationArrow, { ariaLabel: ariaLabelArrowPrev, arrow: "prev", isDisabled: activeIndex === 0 && !arrowLinkPrev, href: arrowLinkPrev, linkComponent: linkComponent, onPageClick: onPageClick, value: activeIndex - 1, pageObject: {
                            index: activeIndex - 1,
                            label: ariaLabelArrowPrev,
                            isActive: arrowLinkPrev || activeIndex > 0
                        } })), children ||
                        visiblePages.map(({ page, pageIndex = 0 }) => {
                            const pageProps = page || getPageProps(pageIndex);
                            const isActive = !pageProps.isDisabled && pageIndex === activeIndex;
                            const usLabel = pageProps.label || pageIndex + 1;
                            const label = locale ? usLabel.toLocaleString(locale) : usLabel.toString();
                            const pageKey = pageProps.key || label;
                            return (_jsx(PaginationPage, { isActive: isActive, label: label, ariaLabel: pageProps.isDisabled ? ariaLabelDisabled : ariaLabelPagePrefix + pageKey, linkComponent: linkComponent, onPageClick: onPageClick, value: pageIndex, hasBorders: hasBorders, "aria-current": isActive, pageObject: {
                                    index: pageIndex,
                                    value: pageIndex,
                                    label,
                                    isActive,
                                    ...pageProps
                                }, ...pageProps }, pageKey));
                        }), hasBorders && (_jsx(PaginationArrow, { arrow: "next", isDisabled: activeIndex === totalPages - 1 && !arrowLinkNext, ariaLabel: ariaLabelArrowNext, href: arrowLinkNext, linkComponent: linkComponent, onPageClick: onPageClick, value: activeIndex + 1, pageObject: {
                            index: activeIndex + 1,
                            label: ariaLabelArrowNext,
                            isActive: arrowLinkNext || activeIndex < totalPages
                        } }))] }), renderCaption && (_jsx(Styled.PaginationCaption, { children: renderCaption({
                    activeIndex,
                    limit,
                    totalPages,
                    visiblePages: visiblePages.length
                }) }))] }));
});
PaginationComponent.displayName = "Pagination";
PaginationComponent.defaultProps = {
    activeIndex: 0,
    ariaLabelArrowNext: "Next",
    ariaLabelArrowPrev: "Previous",
    ariaLabelDisabled: "Disabled",
    ariaLabelPagePrefix: "Go to page ",
    ariaLabelWrapper: "Pagination",
    getPageProps: (index) => ({ id: String(index) }),
    limit: DEFAULT_LIMIT,
    locale: "en-US",
    onPageClick: () => { },
    pages: []
};
export const Pagination = Object.assign(PaginationComponent, {
    Arrow: PaginationArrow,
    Page: PaginationPage
});
